import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  resource,
  signal
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Button} from 'primeng/button';
import {InputTextarea} from 'primeng/inputtextarea';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {MessageService} from 'primeng/api';
import {UserService} from '../../services/user.service';
import {NotificationUserData, SendNotificationCommand, SubscriptionStatus, UserDto} from 'admin-api-dto';

type ExampleDefinition = {
  readonly title: string;
  readonly description: string;
  readonly type: string;
  readonly data?: unknown;
};

type NotificationExample = {
  readonly title: string;
  readonly description: string;
  readonly payload: string;
};

const SAMPLE_LOTTERY_DATA = {
  type: 'regular',
  imageUrl: 'https://images.example.com/lottery.jpg',
  name: 'Harbor View Apartments',
  eligibleUnits: [
    {
      type: 'regular',
      unitType: 'one-bedroom',
      unitsAvailable: 2,
      minIncome: 45000,
      maxIncome: 85000,
      monthlyRent: 1800,
      estimatedSavings: 350,
    },
  ],
  address: '100 Example Ave, Brooklyn, NY',
  unitsAvailable: 12,
  closingDate: '2025-12-31',
  id: 'lottery-1',
  slug: 'harbor-view-apartments',
};

const SAMPLE_LOTTERY_NOTIFICATION_DATA = {
  eligibilityType: 'certain',
  lotteryData: SAMPLE_LOTTERY_DATA,
  autoApplyEnabled: true,
  simplifiedPaymentStatus: 'active',
};

const SAMPLE_LOTTERY_NOTIFICATION_DATA_WITH_TRIAL = {
  ...SAMPLE_LOTTERY_NOTIFICATION_DATA,
  hasTrial: true,
};

const EXAMPLE_DEFINITIONS: ExampleDefinition[] = [
  {
    title: 'New eligible lotteries',
    description: 'Let users know about recently opened lotteries they qualify for.',
    type: 'new-eligible-lotteries',
    data: SAMPLE_LOTTERY_NOTIFICATION_DATA_WITH_TRIAL,
  },
  {
    title: 'New closed applications',
    description: 'Alert users when new lotteries close.',
    type: 'new-closed-applications',
    data: SAMPLE_LOTTERY_NOTIFICATION_DATA,
  },
  {
    title: 'Lottery closing soon',
    description: 'Deadline reminders for upcoming opportunities.',
    type: 'lottery-about-to-close',
    data: SAMPLE_LOTTERY_NOTIFICATION_DATA_WITH_TRIAL,
  },
  {
    title: 'Lottery number received',
    description: 'Shares an assigned lottery number.',
    type: 'lottery-number-received',
    data: {
      lotteryNumber: '1234',
      ...SAMPLE_LOTTERY_NOTIFICATION_DATA,
    },
  },
  {
    title: 'Not selected',
    description: 'Confirms a non-selection result.',
    type: 'not-selected',
    data: {
      lotteryNumber: '5678',
      ...SAMPLE_LOTTERY_NOTIFICATION_DATA,
    },
  },
  {
    title: 'Selected',
    description: 'Celebrates a win in the lottery.',
    type: 'selected',
    data: {
      lotteryNumber: '9012',
      ...SAMPLE_LOTTERY_NOTIFICATION_DATA,
    },
  },
  {
    title: 'Message',
    description: 'General in-app message with title/body.',
    type: 'message',
    data: {
      title: 'Important update',
      message: 'Check the home screen for the latest steps.',
    },
  },
  {
    title: 'Unknown email',
    description: 'Reports missing email attachments via S3.',
    type: 'unknown-email',
    data: {
      s3Key: 'emails/unknown/12345.json',
      shouldRemind: true,
    },
  },
  {
    title: 'Unknown email reminder',
    description: 'Reminder for missing email attachments.',
    type: 'unknown-email-reminder',
    data: {
      originalNotificationId: 'unknown-email-12345',
      s3Key: 'emails/unknown/12345.json',
    },
  },
  {
    title: 'Custom SMS/text',
    description: 'Ad-hoc text message generator.',
    type: 'custom',
    data: {
      textMessage: 'Custom alert for you!',
      emailTemplate: undefined,
      inAppMessage: undefined,
      timeSensitive: true,
    },
  },
  {
    title: 'Applications submitted',
    description: 'Reports manual submissions.',
    type: 'applications-submitted',
    data: {
      lotteriesData: [SAMPLE_LOTTERY_DATA],
      applicationMethod: 'manual-apply',
    },
  },
  {
    title: 'New user',
    description: 'Signals a fresh user registration.',
    type: 'new-user',
    data: {},
  },
  {
    title: 'Auth linked to another user',
    description: 'Security notice for linked auth data.',
    type: 'auth-linked-to-another-user',
    data: {},
  },
  {
    title: 'Mandatory flow not complete',
    description: 'First reminder to complete the mandatory flow.',
    type: 'mandatory-flow-not-complete',
    data: {},
  },
  {
    title: 'Mandatory flow not complete 2',
    description: 'Second reminder variant.',
    type: 'mandatory-flow-not-complete-2',
    data: {},
  },
  {
    title: 'Mandatory flow not complete 3',
    description: 'Third reminder variant.',
    type: 'mandatory-flow-not-complete-3',
    data: {},
  },
  {
    title: 'Mandatory flow not complete 4',
    description: 'Fourth reminder variant.',
    type: 'mandatory-flow-not-complete-4',
    data: {},
  },
  {
    title: 'Mandatory flow not complete recurring',
    description: 'Recurring reminder.',
    type: 'mandatory-flow-not-complete-recurring',
    data: {},
  },
  {
    title: 'Mandatory flow completed',
    description: 'Highlights completion.',
    type: 'mandatory-flow-completed',
    data: {},
  },
  {
    title: 'Payment failed (stripe)',
    description: 'Stripe payment failure with a pay link.',
    type: 'payment-failed',
    data: {
      type: 'stripe',
      payUrl: 'https://pay.example.com',
      retryCount: 1,
      userStatus: 'active',
      eligibilityPrediction: 0.82,
      recentApplicationsLotteryData: [SAMPLE_LOTTERY_DATA],
    },
  },
  {
    title: 'Payment failed (iOS)',
    description: 'iOS payment failure.',
    type: 'payment-failed',
    data: {
      type: 'ios',
    },
  },
  {
    title: 'Did not pay',
    description: 'Initial unpaid reminder.',
    type: 'did-not-pay',
    data: {
      paymentStatus: 'initial',
      hasTrial: true,
    },
  },
  {
    title: 'Did not pay 2',
    description: 'Follow-up reminder.',
    type: 'did-not-pay-2',
    data: {
      paymentStatus: 'initial',
      hasTrial: true,
    },
  },
  {
    title: 'Did not pay 3',
    description: 'Third reminder chance.',
    type: 'did-not-pay-3',
    data: {
      paymentStatus: 'initial',
      hasTrial: true,
    },
  },
  {
    title: 'Did not pay 4',
    description: 'Fourth reminder iteration.',
    type: 'did-not-pay-4',
    data: {
      paymentStatus: 'initial',
      hasTrial: true,
    },
  },
  {
    title: 'Did not pay 5',
    description: 'Final reminder before action.',
    type: 'did-not-pay-5',
    data: {
      paymentStatus: 'initial',
      hasTrial: true,
    },
  },
  {
    title: 'Trial will end',
    description: 'Trial expiration reminder.',
    type: 'trial-will-end',
    data: {
      expirationDate: '2025-12-31',
      userStatus: 'mandatory-flow',
      eligibilityPrediction: 0.65,
      recentApplicationsLotteryData: [SAMPLE_LOTTERY_DATA],
    },
  },
  {
    title: 'Yearly renewal reminder',
    description: 'Notifies about annual renewal.',
    type: 'subscription-yearly-renewal-reminder',
    data: {renewalDate: '2025-11-11'},
  },
  {
    title: 'Subscription cancelled',
    description: 'Confirms cancellation.',
    type: 'subscription-cancelled',
    data: {},
  },
  {
    title: 'Subscription will cancel',
    description: 'Notifies about upcoming cancellation.',
    type: 'subscription-will-cancel',
    data: {
      cancellationDate: '2025-12-15',
    },
  },
  {
    title: 'Monthly recap',
    description: 'Summarizes recent activity.',
    type: 'monthly-recap',
    data: {
      month: 11,
      year: 2025,
      shouldSend: false,
      waitingForCloseCount: 0,
      waitingForNumberCount: 0,
      numberReceivedCount: 0,
    },
  },
];

@Component({
  selector: 'app-notification-test-page',
  imports: [
    CommonModule,
    Button,
    InputTextarea
  ],
  templateUrl: './notification-test-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationTestPageComponent {
  public readonly userId = input.required<string>();

  private static readonly DEFAULT_PAYLOAD = JSON.stringify({
    type: 'custom',
    data: {
      textMessage: 'Test notification from admin',
      timeSensitive: true
    }
  }, null, 2);
  private static readonly DEFAULT_USER_DATA = JSON.stringify({
    firstName: 'Sample',
    fullName: 'Sample User',
    referralUrl: 'https://www.myhomey.io/referral/sample',
    anonymousId: 'anon-123',
    lastSeen: new Date().toISOString(),
    id: 'user-123',
    simplifiedPaymentStatus: 'active',
    subscription: {
      type: 'paid',
      subscriptionInterval: 'month',
      currentSubscriptionPrice: 25,
    },
  }, null, 2);

  protected readonly examplePayload = NotificationTestPageComponent.DEFAULT_PAYLOAD;
  protected readonly userDataExample = NotificationTestPageComponent.DEFAULT_USER_DATA;
  protected readonly jsonInput = signal(NotificationTestPageComponent.DEFAULT_PAYLOAD);
  protected readonly userDataInput = signal('');
  protected readonly validationError = signal<string | undefined>(undefined);
  protected readonly loading = signal(false);
  protected readonly examples: NotificationExample[] = EXAMPLE_DEFINITIONS.map(example => ({
    title: example.title,
    description: example.description,
    payload: JSON.stringify(
      example.data === undefined
        ? {type: example.type}
        : {type: example.type, data: example.data},
      null,
      2
    ),
  }));

  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly messageService = inject(MessageService);
  private readonly user = resource<UserDto, { id: string }>({
    request: () => ({id: this.userId()}),
    loader: ({request: {id}}) => firstValueFrom(this.userService.getUser(id))
  });

  constructor() {
    effect(
      () => {
        const user = this.user.value();
        if (!user || this.userDataInput().trim().length > 0) {
          return;
        }
        this.userDataInput.set(JSON.stringify(this.buildUserData(user), null, 2));
      },
      {allowSignalWrites: true}
    );
  }

  protected resetJson() {
    this.validationError.set(undefined);
    this.jsonInput.set(NotificationTestPageComponent.DEFAULT_PAYLOAD);
    this.userDataInput.set('');
  }

  protected goBack() {
    this.router.navigate(['/users', this.userId()]);
  }

  protected async sendTestNotification() {
    this.validationError.set(undefined);
    let parsedPayload: unknown;

    try {
      parsedPayload = JSON.parse(this.jsonInput());
    } catch {
      this.validationError.set('Provided value is not valid JSON.');
      return;
    }

    const command: SendNotificationCommand = parsedPayload
      && typeof parsedPayload === 'object'
      && 'information' in parsedPayload
      ? (parsedPayload as SendNotificationCommand)
      : {
        information: parsedPayload as SendNotificationCommand['information']
      };
    const userDataValue = this.userDataInput().trim();
    let userData: SendNotificationCommand['userData'];
    if (userDataValue.length > 0) {
      try {
        userData = JSON.parse(userDataValue) as SendNotificationCommand['userData'];
      } catch {
        this.validationError.set('Provided user data is not valid JSON.');
        return;
      }
    }

    this.loading.set(true);

    try {
      await firstValueFrom(
        this.userService.sendNotification(this.userId(), {
          ...command,
          ...(userData ? {userData} : {}),
        })
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Notification queued',
        detail: 'Test notification created for the user.'
      });
      this.resetJson();
    } catch (error) {
      this.validationError.set(this.getErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.normalizeServerMessage(error.error);
      if (serverMessage) {
        return serverMessage;
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unable to create notification.';
  }

  private normalizeServerMessage(payload: unknown): string | undefined {
    if (!payload) {
      return undefined;
    }

    if (typeof payload === 'string') {
      return payload;
    }

    if (typeof payload === 'object' && 'message' in payload) {
      const value = (payload as { message?: unknown }).message;
      if (typeof value === 'string') {
        return value;
      }
    }

    return undefined;
  }

  private buildUserData(user: UserDto): NotificationUserData {
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    const simplifiedPaymentStatus = this.mapSimplifiedPaymentStatus(user.subscription?.status);
    const isPaid = user.subscription?.paymentStatus === 'paid';
    return {
      firstName: user.firstName,
      fullName,
      referralUrl: '',
      anonymousId: '',
      lastSeen: user.lastSeen,
      id: user.id,
      simplifiedPaymentStatus,
      subscription: isPaid
        ? {
          type: 'paid',
          subscriptionInterval: 'month',
          currentSubscriptionPrice: undefined,
        }
        : {type: 'none'},
    };
  }

  private mapSimplifiedPaymentStatus(status?: SubscriptionStatus): NotificationUserData['simplifiedPaymentStatus'] {
    switch (status) {
      case 'active':
        return 'active';
      case 'trialing':
        return 'trialing';
      case 'past_due':
        return 'past-due';
      case 'canceled':
        return 'canceled';
      case 'payment-pending':
      case 'incomplete':
      case 'incomplete_expired':
      case 'unpaid':
        return 'not-paid-yet';
      case 'paused':
        return 'canceling';
      default:
        return 'not-paid-yet';
    }
  }
}
